export type StorageProvider = () => Storage;

type VersionedValue<T> = {
  version: number;
  data: T;
};

type VersionedStorageOptions<T> = {
  key: string;
  version: number;
  storage: StorageProvider;
  validate: (value: unknown) => value is T;
  maxBytes: number;
  migrateLegacy?: (value: unknown) => T | undefined;
};

export type ScalarStorageOptions = {
  key: string;
  storage: StorageProvider;
  maxBytes: number;
  validate?: (value: string) => boolean;
  deleteInvalid?: boolean;
};

const encoder = new TextEncoder();

function byteLength(value: string) {
  return encoder.encode(value).byteLength;
}

function storageInstance(provider: StorageProvider) {
  try {
    return provider();
  } catch {
    return undefined;
  }
}

export function removeStoredValue(provider: StorageProvider, key: string) {
  const storage = storageInstance(provider);
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function readStoredString(options: ScalarStorageOptions): string | undefined {
  const storage = storageInstance(options.storage);
  if (!storage) return undefined;
  let value: string | null;
  try {
    value = storage.getItem(options.key);
  } catch {
    return undefined;
  }
  if (value === null) return undefined;
  if (byteLength(value) <= options.maxBytes && (!options.validate || options.validate(value))) return value;
  if (options.deleteInvalid !== false) removeStoredValue(options.storage, options.key);
  return undefined;
}

export function writeStoredString(options: ScalarStorageOptions, value: string) {
  if (byteLength(value) > options.maxBytes || (options.validate && !options.validate(value))) return false;
  const storage = storageInstance(options.storage);
  if (!storage) return false;
  try {
    storage.setItem(options.key, value);
    return true;
  } catch {
    return false;
  }
}

export function readStoredNumber(options: ScalarStorageOptions): number | undefined {
  const raw = readStoredString(options);
  if (raw === undefined || raw.trim() === '') return undefined;
  const value = Number(raw);
  if (Number.isFinite(value)) return value;
  if (options.deleteInvalid !== false) removeStoredValue(options.storage, options.key);
  return undefined;
}

export function writeStoredNumber(options: ScalarStorageOptions, value: number) {
  if (!Number.isFinite(value)) return false;
  return writeStoredString(options, String(value));
}

export function readVersionedJson<T>(options: VersionedStorageOptions<T>): T | undefined {
  const storage = storageInstance(options.storage);
  if (!storage) return undefined;

  let raw: string | null;
  try {
    raw = storage.getItem(options.key);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  if (byteLength(raw) > options.maxBytes) {
    removeStoredValue(options.storage, options.key);
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeStoredValue(options.storage, options.key);
    return undefined;
  }

  if (typeof parsed === 'object' && parsed !== null && 'version' in parsed && 'data' in parsed) {
    const envelope = parsed as Partial<VersionedValue<unknown>>;
    if (envelope.version === options.version && options.validate(envelope.data)) return envelope.data;
    removeStoredValue(options.storage, options.key);
    return undefined;
  }

  const migrated = options.migrateLegacy?.(parsed);
  if (migrated === undefined || !options.validate(migrated)) {
    removeStoredValue(options.storage, options.key);
    return undefined;
  }
  writeVersionedJson(options, migrated);
  return migrated;
}

export function writeVersionedJson<T>(options: VersionedStorageOptions<T>, value: T) {
  if (!options.validate(value)) return false;
  const serialized = JSON.stringify({ version: options.version, data: value } satisfies VersionedValue<T>);
  if (byteLength(serialized) > options.maxBytes) return false;
  const storage = storageInstance(options.storage);
  if (!storage) return false;
  try {
    storage.setItem(options.key, serialized);
    return true;
  } catch {
    return false;
  }
}

export const RECENT_SEARCHES_KEY = 'vennevent:recent-searches';
export const MAX_RECENT_SEARCHES = 6;
export const MAX_RECENT_SEARCH_LENGTH = 120;
const RECENT_SEARCHES_MAX_BYTES = 2_048;

export function normalizeRecentSearches(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const term = item.normalize('NFKC').trim().replace(/\s+/g, ' ').slice(0, MAX_RECENT_SEARCH_LENGTH);
    if (term.length < 2) continue;
    const identity = term.toLocaleLowerCase();
    if (seen.has(identity)) continue;
    seen.add(identity);
    normalized.push(term);
    if (normalized.length === MAX_RECENT_SEARCHES) break;
  }
  return normalized;
}

function isRecentSearches(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= MAX_RECENT_SEARCHES &&
    value.every((item) => typeof item === 'string' && item.length >= 2 && item.length <= MAX_RECENT_SEARCH_LENGTH)
  );
}

function recentSearchOptions(storage: StorageProvider): VersionedStorageOptions<string[]> {
  return {
    key: RECENT_SEARCHES_KEY,
    version: 1,
    storage,
    validate: isRecentSearches,
    maxBytes: RECENT_SEARCHES_MAX_BYTES,
    migrateLegacy(value) {
      if (!Array.isArray(value)) return undefined;
      return normalizeRecentSearches(value);
    },
  };
}

export function readRecentSearches(storage: StorageProvider = () => window.localStorage) {
  return readVersionedJson(recentSearchOptions(storage)) ?? [];
}

export function writeRecentSearches(value: unknown, storage: StorageProvider = () => window.localStorage) {
  const normalized = normalizeRecentSearches(value);
  return { value: normalized, stored: writeVersionedJson(recentSearchOptions(storage), normalized) };
}

export function clearRecentSearches(storage: StorageProvider = () => window.localStorage) {
  return removeStoredValue(storage, RECENT_SEARCHES_KEY);
}

export type LocationRequestCooldown = {
  until: number;
  failures: number;
  reason: 'rate-limit' | 'provider' | 'network';
};

export const LOCATION_COOLDOWN_KEY = 'vennevent:location-cooldown';

function isLocationRequestCooldown(value: unknown): value is LocationRequestCooldown {
  if (!value || typeof value !== 'object') return false;
  const cooldown = value as Partial<LocationRequestCooldown>;
  return (
    typeof cooldown.until === 'number' &&
    Number.isFinite(cooldown.until) &&
    Number.isInteger(cooldown.failures) &&
    cooldown.failures! >= 1 &&
    cooldown.failures! <= 16 &&
    (cooldown.reason === 'rate-limit' || cooldown.reason === 'provider' || cooldown.reason === 'network')
  );
}

function locationCooldownOptions(storage: StorageProvider): VersionedStorageOptions<LocationRequestCooldown> {
  return {
    key: LOCATION_COOLDOWN_KEY,
    version: 1,
    storage,
    validate: isLocationRequestCooldown,
    maxBytes: 256,
  };
}

export function readLocationRequestCooldown(now = Date.now(), storage: StorageProvider = () => window.sessionStorage) {
  const cooldown = readVersionedJson(locationCooldownOptions(storage));
  if (!cooldown) return undefined;
  if (cooldown.until <= now) {
    removeStoredValue(storage, LOCATION_COOLDOWN_KEY);
    return undefined;
  }
  return cooldown;
}

export function writeLocationRequestCooldown(value: LocationRequestCooldown, storage: StorageProvider = () => window.sessionStorage) {
  return writeVersionedJson(locationCooldownOptions(storage), value);
}

export function clearLocationRequestCooldown(storage: StorageProvider = () => window.sessionStorage) {
  return removeStoredValue(storage, LOCATION_COOLDOWN_KEY);
}
